import { useQuery } from '@tanstack/react-query';
import { api } from '../services/api';

/**
 * Query to get user details
 * @param {boolean} enabled - Whether the query should be active
 * @param {function} onSuccess - Callback when user details are fetched successfully
 * @param {function} onError - Callback when fetching user details fails
 */
export const useUserDetails = (enabled = true, onSuccess, onError) => {
  return useQuery({
    queryKey: ['user-details'],
    queryFn: async () => {
      const result = await api.getUserDetails();
      if (result?.error) {
        throw new Error(result.message || 'Failed to get user details');
      }
      return result;
    },
    enabled,
    onSuccess: (data) => {
      if (onSuccess) onSuccess(data);
    },
    onError: (error) => {
      console.error('User details fetch error:', error);
      if (onError) onError(error);
    },
    retry: 1,
    staleTime: 0, // Consider data fresh for 5 minutes
  });
};

/**
 * Query to get user identity
 * @param {boolean} enabled - Whether the query should be active
 * @param {function} onSuccess - Callback when identity is fetched successfully
 * @param {function} onError - Callback when fetching identity fails
 */
export const useUserIdentity = (enabled = true, onSuccess, onError) => {
  return useQuery({
    queryKey: ['user-identity'],
    queryFn: async () => {
      const result = await api.getUserIdentity();
      if (result?.error) {
        throw new Error(result.message || 'Failed to get user identity');
      }
      return result;
    },
    enabled,
    onSuccess: (data) => {
      if (onSuccess) onSuccess(data);
    },
    onError: (error) => {
      console.error('User identity fetch error:', error);
      if (onError) onError(error);
    },
    retry: 1,
    staleTime: 5 * 60 * 1000, // Consider data fresh for 5 minutes
  });
};

/**
 * Query to get user subscription
 * @param {boolean} enabled - Whether the query should be active
 * @param {function} onSuccess - Callback when subscription is fetched successfully
 * @param {function} onError - Callback when fetching subscription fails
 */
export const useUserSubscription = (enabled = true, onSuccess, onError) => {
  return useQuery({
    queryKey: ['user-subscription'],
    queryFn: async () => {
      const result = await api.getUserSubscription();
      if (result?.error) {
        throw new Error(result.message || 'Failed to get user subscription');
      }
      return result;
    },
    enabled,
    onSuccess: (data) => {
      if (onSuccess) onSuccess(data);
    },
    onError: (error) => {
      console.error('User subscription fetch error:', error);
      if (onError) onError(error);
    },
    retry: 1,
    staleTime: 0, // Consider data fresh for 5 minutes
    cacheTime: 0, // Cache data for 5 minutes
  });
};
